import React, { useState } from 'react';

import { useIntl } from 'react-intl-next';

// eslint-disable-next-line @atlaskit/design-system/no-banned-imports
import { useId } from '@atlaskit/ds-lib/use-id';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	getInputSkeletonFocus,
	resetInputSkeletonState,
	SEARCH_QUERY_KEY,
	setInputSkeletonIsFocus,
	setInputSkeletonQuery,
	setInputSkeletonSelection,
	setSessionItem,
} from '@atlassian/search-dialog';

import { Textfield } from '../../../common/ui/quick-find/textfield';
import { getInitialQuery } from '../../../common/ui/quick-find/textfield/input-skeleton';

import { messages } from './messages';

export type QuickFindSkeletonProps = {
	onEnter?: (e: React.KeyboardEvent) => void;
	isNav4Enabled?: boolean;
	injectValue?: string;
	customPlaceholder?: string;
	inputRef?: React.RefObject<HTMLInputElement>;
};

if (!window.inputSkeletonState) {
	resetInputSkeletonState();
}

export const QuickFindSkeleton = ({
	onEnter,
	isNav4Enabled,
	injectValue,
	customPlaceholder,
	inputRef,
}: QuickFindSkeletonProps): React.JSX.Element => {
	const dialogId = useId();
	const { formatMessage } = useIntl();
	const initialSearchQuery = getInitialQuery();

	// Use the stored state as initial values for expansion and query
	const [isExpanded, setIsExpanded] = useState(getInputSkeletonFocus);
	const [value, setValue] = useState(() => injectValue ?? initialSearchQuery);

	const onFocus = (e: React.FocusEvent<HTMLInputElement>) => {
		setIsExpanded(true);
		setInputSkeletonIsFocus(true);
		const target = e.currentTarget;
		setInputSkeletonSelection(
			target.selectionStart || 0,
			target.selectionEnd || 0,
			target.selectionDirection || 'none',
		);
	};

	const onBlur = () => {
		setIsExpanded(false);
		setInputSkeletonIsFocus(false);
		setInputSkeletonSelection(0, 0, 'none');
	};

	const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
		const target = e.currentTarget;
		if (e.key === 'Escape') {
			target.blur();
			onBlur();
		}
	};

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.currentTarget.value;
		setValue(newValue);
		setInputSkeletonQuery(newValue);
		const target = e.currentTarget;
		setInputSkeletonSelection(
			target.selectionStart || 0,
			target.selectionEnd || 0,
			target.selectionDirection || 'none',
		);
		setSessionItem(SEARCH_QUERY_KEY, newValue);
	};

	const onSelect = (e: React.UIEvent<HTMLInputElement>) => {
		const target = e.currentTarget;
		setInputSkeletonSelection(
			target.selectionStart || 0,
			target.selectionEnd || 0,
			target.selectionDirection || 'none',
		);
	};

	return (
		<Textfield
			value={value}
			placeholder={fg('quickfind-ghosttext')
				? (customPlaceholder ?? formatMessage(messages.placeholder))
				: formatMessage(messages.placeholder)}
			dialogId={dialogId}
			quickFindOpen={isExpanded}
			onEnter={onEnter}
			onFocus={onFocus}
			onBlur={onBlur}
			onSelect={onSelect}
			onKeyDown={onKeyDown}
			onChange={onChange}
			isNav4Enabled={isNav4Enabled}
			inputRef={inputRef}
		/>
	);
};
